import {
  User,
  BookOpen,
  Scale,
  ScrollText,
  BarChart3,
  LogIn,
  Layers,
  KeyRound,
  FileCheck,
  Users,
  PlusCircle,
  Receipt,
  LogOut,
  Volume2,
  VolumeX,
  Sun,
  Moon,
  History,
} from "lucide-react";
import useSize from "../../../Utils/useSize/useSize";
import Backend from "../../../Backend/Backend";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useCallback, useEffect, useState } from "react";
import Loading from "../../../Utils/Loading/Loading";
import TopCard from "./TopCard";

export default function ProfileSettings() {
  const windowSize = useSize();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [backend, setBackend] = useState(null);

  // ⚙️ User preferences
  const [soundOn, setSoundOn] = useState(
    JSON.parse(localStorage.getItem("sound")),
  );
  const [darkTheme, setDarkMode] = useState(
    localStorage.getItem("themeType") === "1",
  );

  // Initialize backend once
  useEffect(() => {
    (async () => {
      const b = await new Backend().init();
      setBackend(b);
    })();
  }, []);

  // Load profile data
  useEffect(() => {
    if (backend) loadProfile();
  }, [backend]);

  useEffect(() => {
    document.body.dataset.theme = darkTheme ? "dark" : "light";
    localStorage.setItem("themeType", darkTheme ? "1" : "0");
  }, [darkTheme]);

  const loadProfile = async () => {
    setLoading(true);
    const data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
    };

    try {
      const response = await backend.profile(data);
      if (response.error === "False") {
        setProfile(response.profile);
      } else {
        toast.error(response.message);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load profile.");
    } finally {
      setLoading(false);
    }
  };

  const toggleTheme = useCallback(() => setDarkMode((prev) => !prev), []);

  // 🔊 Toggle Sound
  const toggleSound = () => {
    const newSoundState = !soundOn;
    setSoundOn(newSoundState);
    localStorage.setItem("sound", newSoundState);
    toast.success(`Sound turned ${newSoundState ? "On" : "Off"}`);
  };

  // 🧩 Menu with routes
  const menuSections = [
    {
      title: "Account",
      items: [
        // {
        //   icon: <PlusCircle className="text-green-600" />,
        //   title: "New Account",
        //   route: "/new-account",
        // },
        ...(profile?.intraday_sq === 1
          ? [
              {
                icon: <History className="text-green-600" />,
          title: "Transaction History",
          route: "/TransactionHistory",
              },
            ]
          : []),
        {
          icon: <Scale className="text-blue-600" />,
          title: "Settlement",
          route: "/Settlement",
        },
        {
          icon: <BookOpen className="text-orange-500" />,
          title: "Ledger",
          route: "/Ledger",
        },
      ],
    },
    {
      title: "Reports & Logs",
      items: [
        ...(profile?.nse_cash === 1
          ? [
              {
                icon: <Receipt className="text-red-500" />,
                title: "Interest Bill",
                route: "/InterestBill",
              },
            ]
          : []),
        {
          icon: <FileCheck className="text-gray-600" />,
          title: "Generate Bill",
          route: "/GenerateBill",
        },
        {
          icon: <ScrollText className="text-purple-500" />,
          title: "Rules",
          route: "/Rules",
        },
        {
          icon: <BarChart3 className="text-blue-500" />,
          title: "Symbol Log",
          route: "/SymbolLogs",
        },
        {
          icon: <LogIn className="text-teal-500" />,
          title: "Login Logs",
          route: "/LoginLogs",
        },
        {
          icon: <Layers className="text-amber-600" />,
          title: "Symbol Quantity",
          route: "/SymbolQty",
        },
      ],
    },
    {
      title: "Security & Others",
      items: [
        {
          icon: <KeyRound className="text-red-500" />,
          title: "Change Password",
          route: "/ChangePassword",
        },
        {
          icon: <Users className="text-pink-500" />,
          title: "Invite Friends",
          action: "invite",
        },
        {
          icon: <User className="text-blue-600" />,
          title: "Profile",
          route: "/ProfileUser",
        },
        {
          icon: soundOn ? (
            <Volume2 className="text-green-500" />
          ) : (
            <VolumeX className="text-gray-500" />
          ),
          title: soundOn ? "Sound: On" : "Sound: Off",
          action: "toggleSound",
        },
        {
          icon: !darkTheme ? (
            <Moon className="text-yellow-400" />
          ) : (
            <Sun className="text-blue-500" />
          ),
          title: !darkTheme ? "Dark Mode" : "Light Mode",
          action: "toggleTheme",
        },
      ],
    },
  ];

  if (loading || !profile) return <Loading height={windowSize[1] - 100} />;

  // 📲 Handle menu clicks
  const handleMenuClick = (item) => {
    if (item.action === "invite") {
      const message = encodeURIComponent(
        `👋 Hey! I'm using *${window.APP_NAME}* to paper trade and learn trading risk-free.\n\n💹 Start here 👉 ${window.WEBSITE_LINK}`,
      );
      const whatsappUrl = `https://wa.me/?text=${message}`;
      window.open(whatsappUrl, "_blank");
      return;
    }

    if (item.action === "toggleTheme") {
      toggleTheme();
      return;
    }

    if (item.action === "toggleSound") {
      toggleSound();
      return;
    }

    if (item.route) navigate(item.route);
  };

  return (
    <div
      style={{
        height: windowSize[1],
        overflowY: "auto",
        paddingBottom: 200,
      }}
      className="bg-[var(--background)] py-2 px-4 flex scroll-overflow-y flex-col items-center"
    >
      {/* 🧑 Profile Card */}
      <div className="bg-[var(--background)] rounded-2xl shadow-sm  dark:border-[var(--hover)] w-full relative">
        <TopCard profile={profile} />
      </div>

      {/* ⚙️ Menu Sections */}
      <div className="w-full max-w-md mt-4 space-y-4">
        {menuSections.map((section, sIndex) => (
          <div
            key={sIndex}
            className="bg-[var(--background)] rounded-2xl shadow-sm border-2 border-[var(--hover)] overflow-hidden"
          >
            <div className="px-4 py-2 border-b border-[var(--hover)] text-[13px] font-semibold text-[var(--text)] uppercase tracking-wide">
              {section.title}
            </div>

            {section.items.map((item, iIndex) => (
              <div
                key={iIndex}
                onClick={() => handleMenuClick(item)}
                className="flex items-center justify-between px-4 py-3 transition cursor-pointer hover:bg-[var(--hoverlight)]"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6">{item.icon}</div>
                  <div className="text-[15px] font-medium text-[var(--text)] dark:text-gray-100">
                    {item.title}
                  </div>
                </div>
                <div className="text-[var(--text)]">›</div>
              </div>
            ))}
          </div>
        ))}

        {/* 🚪 Logout */}
        <div
          onClick={() => window.LogOut?.()}
          className="bg-[var(--background)] rounded-2xl shadow-sm border-2 border-[var(--hover)] overflow-hidden cursor-pointer transition hover:bg-[var(--hoverlight)]"
        >
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-3">
              <LogOut className="text-red-500" />
              <div className="text-[15px] font-semibold text-red-600 dark:text-red-400">
                Logout
              </div>
            </div>
            <div className="text-gray-400">›</div>
          </div>
        </div>
      </div>
    </div>
  );
}
